import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Button, message, Alert, Spin } from "antd";
import { SafetyCertificateOutlined, MailOutlined, ReloadOutlined } from "@ant-design/icons";
import ContractService from "src/services/ContractService";

export default function OtpModal({
  open,
  contractId,
  onCancel,
  onVerifySuccess,
  signerEmail = "",
  signerName = "",
  contractCode = "",
}) {
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [targetEmail, setTargetEmail] = useState(signerEmail);
  const [maskedEmail, setMaskedEmail] = useState("");
  const inputRefs = useRef([]);

  // Mask email để bảo mật thông tin
  const maskEmail = (email) => {
    if (!email) return "nhansu@cis.com.vn";
    const [name, domain] = email.split("@");
    if (!domain) return email;
    const maskedName = name.length > 3 ? `${name.substring(0, 2)}***${name.slice(-1)}` : name;
    return `${maskedName}@${domain}`;
  };

  // Gửi OTP qua email nhân sự trong bảng personnels
  const requestOtpFromBackend = useCallback(async (isResend = false) => {
    if (!contractId) return;
    setIsSending(true);
    try {
      const res = await ContractService.sendContractOtp(contractId);
      const data = res?.data || res?.success || res;
      if (data && data.email) {
        setTargetEmail(data.email);
        setMaskedEmail(data.masked_email || maskEmail(data.email));
        if (isResend) {
          message.success(`Đã gửi lại mã OTP về email ${data.masked_email || data.email}`);
        }
      }
    } catch (err) {
      console.error("Gửi OTP thất bại:", err);
      if (isResend) {
        message.warning("Không thể gửi email lúc này. Vui lòng thử lại sau.");
      }
    } finally {
      setIsSending(false);
    }
  }, [contractId]);

  // Init or reset OTP
  useEffect(() => {
    if (open) {
      setOtpDigits(["", "", "", "", "", ""]);
      setCountdown(60);
      setCanResend(false);
      setMaskedEmail(maskEmail(signerEmail));
      setTargetEmail(signerEmail);

      // Gọi API gửi OTP thực tế về email trong bảng personnels
      requestOtpFromBackend(false);

      // Tự động focus ô đầu tiên
      setTimeout(() => {
        if (inputRefs.current[0]) inputRefs.current[0].focus();
      }, 200);
    }
  }, [open, signerEmail, requestOtpFromBackend]);

  // Bộ đếm lùi
  useEffect(() => {
    if (!open) return;
    if (countdown > 0) {
      const timer = setInterval(() => setCountdown((c) => c - 1), 1000);
      return () => clearInterval(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown, open]);

  // Xử lý nhập từng ô OTP
  const handleChangeDigit = (index, value) => {
    const cleanVal = value.replace(/\D/g, "");
    if (!cleanVal && value !== "") return;

    const newDigits = [...otpDigits];
    newDigits[index] = cleanVal.slice(-1);
    setOtpDigits(newDigits);

    if (cleanVal && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0 && inputRefs.current[index - 1]) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasteData) return;
    const newDigits = [...otpDigits];
    for (let i = 0; i < pasteData.length; i++) {
      newDigits[i] = pasteData[i];
    }
    setOtpDigits(newDigits);
    if (pasteData.length === 6 && inputRefs.current[5]) {
      inputRefs.current[5].focus();
    }
  };

  const handleResend = () => {
    setCountdown(60);
    setCanResend(false);
    setOtpDigits(["", "", "", "", "", ""]);
    requestOtpFromBackend(true);
    if (inputRefs.current[0]) inputRefs.current[0].focus();
  };

  const handleVerify = () => {
    const currentCode = otpDigits.join("");
    if (currentCode.length < 6) {
      message.warning("Vui lòng nhập đầy đủ 6 chữ số mã OTP!");
      return;
    }

    onVerifySuccess({
      verifiedAt: new Date().toISOString(),
      verifiedEmail: targetEmail || signerEmail || "nhansu@congty.com",
      otpCode: currentCode,
    });
  };

  const finalDisplayEmail = maskedEmail || maskEmail(targetEmail || signerEmail);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width={480}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Hủy bỏ
        </Button>,
        <Button
          key="verify"
          type="primary"
          icon={<SafetyCertificateOutlined />}
          style={{ background: "#16a34a", borderColor: "#16a34a", fontWeight: 700 }}
          onClick={handleVerify}
          loading={isSending}
        >
          Xác nhận giao kết
        </Button>,
      ]}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <SafetyCertificateOutlined style={{ color: "#16a34a", fontSize: 22 }} />
          <div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>Xác thực ký kết qua Email OTP</div>
            <div style={{ fontSize: 12, color: "#64748b", fontWeight: 400 }}>
              Bảo chứng danh tính giao kết hợp đồng {contractCode ? `(${contractCode})` : ""}
            </div>
          </div>
        </div>
      }
    >
      <Spin spinning={isSending} tip="Đang gửi mã OTP về email nhân sự...">
        <div style={{ padding: "10px 0", textAlign: "center" }}>
          <p style={{ fontSize: 13, color: "#475569", marginBottom: 12 }}>
            Hệ thống đã gửi mã xác thực 6 chữ số đến địa chỉ email trong hồ sơ nhân sự:
            <br />
            <b style={{ color: "#1e293b", fontSize: 15 }}>
              <MailOutlined style={{ marginRight: 6, color: "#2563eb" }} />
              {finalDisplayEmail}
            </b>
          </p>

          {/* Ô nhập 6 chữ số OTP */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 10,
              margin: "24px 0",
            }}
            onPaste={handlePaste}
          >
            {otpDigits.map((digit, idx) => (
              <input
                key={idx}
                ref={(el) => (inputRefs.current[idx] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChangeDigit(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                style={{
                  width: 48,
                  height: 52,
                  fontSize: 22,
                  fontWeight: "bold",
                  textAlign: "center",
                  border: digit ? "2px solid #16a34a" : "2px solid #cbd5e1",
                  borderRadius: 8,
                  outline: "none",
                  background: digit ? "#f0fdf4" : "#fff",
                  transition: "all 0.2s",
                }}
              />
            ))}
          </div>

          {/* Hướng dẫn nhận mã thực tế từ email */}
          <Alert
            message={
              <div style={{ fontSize: 13, textAlign: "left", lineHeight: 1.6 }}>
                📩 Vui lòng mở hộp thư email <b>{finalDisplayEmail}</b> (kiểm tra cả mục Hộp thư đến và Spam/Thư rác) để lấy mã xác thực OTP 6 số do hệ thống gửi tới.
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 18, borderRadius: 8 }}
          />

          {/* Đếm ngược gửi lại mã */}
          <div style={{ fontSize: 13, color: "#64748b" }}>
            {canResend ? (
              <Button type="link" icon={<ReloadOutlined />} onClick={handleResend} style={{ fontWeight: 600 }}>
                Gửi lại mã OTP về email
              </Button>
            ) : (
              <span>
                Gửi lại mã sau: <b style={{ color: "#ea580c" }}>{countdown}s</b>
              </span>
            )}
          </div>
        </div>
      </Spin>
    </Modal>
  );
}
